import React, { memo, useCallback, useMemo } from "react";
import {
  Button,
  HStack,
  ListItem,
  Text,
  UnorderedList,
  VStack,
} from "@chakra-ui/react";
import colors from "app/colors";

import { DynamicRulesIcon, HelpIcon, WorldIcon } from "modules/icons";
import useQueryBuilder from "modules/hooks/use-query-builder";
import UserName from "modules/_shared/user-name";
import { useUsers } from "modules/providers/users-provider";
import { DistributionGroupFragment } from "gql/graphql";
import { getUserFullName, sortUsersByFullName } from "modules/utils/Functions";
import { useDistributionGroups } from "modules/providers/distribution-groups-provider";
import { CALLOUT_KEY } from "modules/utils/Constants";
import { useOverlay } from "modules/providers/overlay-provider";
import { MODAL_TYPE } from "modules/providers/overlay-provider/overlay-provider";

interface IProps {
  distributionGroup: DistributionGroupFragment;
}

const AdminDistributionGroupMembershipCriteria = ({
  distributionGroup,
}: IProps) => {
  const { openModal } = useOverlay();
  const { activeUsers, getUsersById } = useUsers();
  const { getDistributionGroupsById } = useDistributionGroups();

  const { naturalLanguageQuery } = useQueryBuilder({
    query: distributionGroup?.queryBuilder
      ? JSON.parse(distributionGroup.queryBuilder)
      : undefined,
  });

  const usersInAllGroups = useMemo(
    () => activeUsers.filter((user) => user.isAllDistributionGroups),
    [activeUsers],
  );

  const addedUserCount = distributionGroup?.addedUserIdList.length;

  const addedDistributionGroupCount =
    distributionGroup?.distributionGroupIdList.length ?? 0;

  const addedUserNames = useMemo(
    () =>
      getUsersById(distributionGroup?.addedUserIdList || [])
        .sort(sortUsersByFullName)
        .map((user) => getUserFullName(user))
        .join(", "),
    [distributionGroup?.addedUserIdList, getUsersById],
  );

  const groupNames = useMemo(
    () =>
      getDistributionGroupsById(
        distributionGroup?.distributionGroupIdList || [],
      )
        .map((distributionGroup) => `${CALLOUT_KEY}${distributionGroup.name}`)
        .join(", "),
    [distributionGroup?.distributionGroupIdList, getDistributionGroupsById],
  );

  const openMembershipCriteriaHelpModal = useCallback(() => {
    if (distributionGroup.isAllUsers) {
      openModal({
        type: MODAL_TYPE.HELP,
        helpModal: {
          icon: <WorldIcon boxSize={14} fill={colors.blackSecondary} />,
          title: "Global Groups",
          message:
            "These groups include all active users in the organization and new users are automatically added to group.",
        },
      });
      return;
    }
    if (distributionGroup.query) {
      openModal({
        type: MODAL_TYPE.HELP,
        helpModal: {
          icon: <DynamicRulesIcon boxSize={14} fill={colors.blackSecondary} />,
          title: "Dynamic Rules",
          message:
            "Members are added or removed from this group based on matching rules created by the admin.",
        },
      });
    }
  }, [distributionGroup.isAllUsers, distributionGroup.query, openModal]);

  return (
    <VStack
      w="full"
      alignItems="flex-start"
      bg={colors.whiteTertiary}
      p={2}
      borderRadius={4}
    >
      <HStack w="full">
        <DynamicRulesIcon boxSize={4} fill={colors.blackSecondary} />
        <Text fontSize={16} fontWeight={700} color={colors.blackSecondary}>
          Membership Criteria
        </Text>
        {(distributionGroup.isAllUsers || distributionGroup.query) && (
          <Button
            onClick={openMembershipCriteriaHelpModal}
            variant="hover"
            size="xs"
          >
            <HelpIcon fill={colors.gray40} boxSize={4} />
          </Button>
        )}
      </HStack>
      {distributionGroup.isAllUsers ? (
        <HStack spacing={1}>
          <Text fontSize={14} fontWeight={700} color={colors.gray50}>
            Global:
          </Text>
          <Text fontSize={14} fontWeight={500} color={colors.gray50}>
            All Active Users
          </Text>
        </HStack>
      ) : (
        <>
          {(addedUserCount > 0 || addedDistributionGroupCount > 0) && (
            <VStack w="full" alignItems="flex-start" spacing={0}>
              <Text fontSize={14} fontWeight={700} color={colors.gray50}>
                Users & Groups
              </Text>
              <UnorderedList
                pl={4}
                color={colors.gray50}
                fontSize={14}
                fontWeight={500}
              >
                {addedUserCount > 0 && (
                  <ListItem>
                    <Text>Users:{` ${addedUserNames}`}</Text>
                  </ListItem>
                )}
                {addedDistributionGroupCount > 0 && (
                  <ListItem>
                    <Text>Groups:{` ${groupNames}`}</Text>
                  </ListItem>
                )}
              </UnorderedList>
            </VStack>
          )}
          {naturalLanguageQuery && (
            <VStack w="full" alignItems="flex-start" spacing={0}>
              <Text fontSize={14} fontWeight={700} color={colors.gray50}>
                Dynamic Rules
              </Text>
              <UnorderedList
                pl={4}
                color={colors.gray50}
                fontSize={14}
                fontWeight={500}
              >
                <ListItem>{naturalLanguageQuery}</ListItem>
              </UnorderedList>
            </VStack>
          )}
          {usersInAllGroups.length > 0 && (
            <VStack w="full" alignItems="flex-start" spacing={0}>
              <Text fontSize={14} fontWeight={700} color={colors.gray50}>
                Included in All Groups ({usersInAllGroups.length})
              </Text>
              <UnorderedList
                pl={4}
                color={colors.gray50}
                fontSize={14}
                fontWeight={500}
              >
                <ListItem>
                  <Text>
                    {usersInAllGroups.map((user) => (
                      <UserName key={user.id} userId={user.id} />
                    ))}
                  </Text>
                </ListItem>
              </UnorderedList>
            </VStack>
          )}
        </>
      )}
    </VStack>
  );
};

export default memo(AdminDistributionGroupMembershipCriteria);
